import { limparArtefatosDoQuill } from './sanitizarHtmlParaEditor'

const PASSO_INDENT_EM = 3
const PASSO_INDENT_LI_EM = 4.5

const ALINHAMENTO_POR_CLASSE: Record<string, string> = {
  'ql-align-center': 'center',
  'ql-align-right': 'right',
  'ql-align-justify': 'justify',
}

const TAMANHO_POR_CLASSE: Record<string, string> = {
  'ql-size-small': '0.75em',
  'ql-size-large': '1.5em',
  'ql-size-huge': '2.5em',
}

// O Quill zera margin/padding de p/pre/blockquote/h1-h6 (e deixa só padding-left em ol/ul, pro
// marcador da lista) — regra em quill.core.css com o seletor ".ql-editor p, ...". Só tem efeito
// dentro do próprio editor: fora dali (prévia, e-mail realmente enviado) esses blocos voltam pro
// espaçamento padrão do navegador, causando o espaço gigante ao dar Enter.
const ESTILO_BASE_POR_TAG: Record<string, string[]> = {
  P: ['margin:0', 'padding:0'],
  PRE: ['margin:0', 'padding:0'],
  BLOCKQUOTE: ['margin:0', 'padding:0'],
  H1: ['margin:0', 'padding:0'],
  H2: ['margin:0', 'padding:0'],
  H3: ['margin:0', 'padding:0'],
  H4: ['margin:0', 'padding:0'],
  H5: ['margin:0', 'padding:0'],
  H6: ['margin:0', 'padding:0'],
  OL: ['margin:0', 'padding:0', 'padding-left:1.5em'],
  UL: ['margin:0', 'padding:0', 'padding-left:1.5em'],
}

function separarDeclaracoes(estilo: string): string[] {
  const declaracoes: string[] = []
  let atual = ''
  let parenteses = 0
  let aspas = ''

  Array.from(estilo).forEach((caractere) => {
    if (aspas) {
      aspas = caractere === aspas ? '' : aspas
    } else if (caractere === '"' || caractere === '\'') {
      aspas = caractere
    } else if (caractere === '(') {
      parenteses += 1
    } else if (caractere === ')') {
      parenteses -= 1
    }

    if (caractere === ';' && !aspas && parenteses <= 0) {
      declaracoes.push(atual)
      atual = ''
      return
    }

    atual += caractere
  })

  declaracoes.push(atual)

  return declaracoes
}

// Chamada repetida sobre o mesmo HTML (cada digitação) não pode acumular a mesma declaração:
// a última ocorrência de cada propriedade prevalece.
function mesclarDeclaracoes(estiloExistente: string | null, novas: string[]): string {
  const porPropriedade = new Map<string, string>()

  const declaracoes = [...separarDeclaracoes(estiloExistente || ''), ...novas]

  declaracoes.forEach((declaracao) => {
    const separador = declaracao.indexOf(':')

    if (separador < 0) {
      return
    }

    const propriedade = declaracao.slice(0, separador).trim().toLowerCase()
    const valor = declaracao.slice(separador + 1).trim()

    porPropriedade.delete(propriedade)
    porPropriedade.set(propriedade, valor)
  })

  return Array.from(porPropriedade).map(([propriedade, valor]) => `${propriedade}:${valor}`).join(';')
}

// O Quill representa recuo (ql-indent-N), alinhamento (ql-align-*) e tamanho de texto (ql-size-*)
// via classes CSS definidas em quill.core.css com o seletor ".ql-editor .ql-indent-N" — ou seja,
// só fazem efeito dentro do próprio editor. Fora dele (prévia, e-mail realmente enviado) a classe
// não tem estilo nenhum associado e o recuo/alinhamento/tamanho desaparece. Por isso, ao ler o
// HTML do Quill para salvar/enviar, convertemos essas classes (e o reset de margin/padding acima)
// em style inline equivalente, que funciona em qualquer lugar.
export default function normalizarClassesQuillParaHtmlPortavel(html: string): string {
  if (!html) {
    return html
  }

  const doc = new DOMParser().parseFromString(html, 'text/html')

  limparArtefatosDoQuill(doc)

  doc.body.querySelectorAll('*').forEach((elemento) => {
    const estilosPortaveis: string[] = [...(ESTILO_BASE_POR_TAG[elemento.tagName] || [])]

    elemento.classList.forEach((classe) => {
      const nivelIndent = classe.match(/^ql-indent-(\d+)$/)

      if (nivelIndent) {
        const passo = elemento.tagName === 'LI' ? PASSO_INDENT_LI_EM : PASSO_INDENT_EM
        estilosPortaveis.push(`padding-left:${Number(nivelIndent[1]) * passo}em`)
        return
      }

      if (ALINHAMENTO_POR_CLASSE[classe]) {
        estilosPortaveis.push(`text-align:${ALINHAMENTO_POR_CLASSE[classe]}`)
        return
      }

      if (TAMANHO_POR_CLASSE[classe]) {
        estilosPortaveis.push(`font-size:${TAMANHO_POR_CLASSE[classe]}`)
      }
    })

    if (estilosPortaveis.length > 0) {
      const estiloExistente = elemento.getAttribute('style')
      elemento.setAttribute('style', mesclarDeclaracoes(estiloExistente, estilosPortaveis))
    }
  })

  return doc.body.innerHTML
}
